'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '~/lib/utils'
import { LinkWrapper } from './platform-features/feature-card'

interface ShadowhintCardProps {
  className?: string
}

export function ShadowhintCard(props: ShadowhintCardProps) {
  const heroRef = useRef<HTMLDivElement>(null)
  const [revealX, setRevealX] = useState(50)
  const [isReturning, setIsReturning] = useState(false)

  useEffect(() => {
    const hero = heroRef.current

    if (!hero) {
      return
    }

    const setPos = (clientX: number) => {
      const rect = hero.getBoundingClientRect()
      let pct = ((clientX - rect.left) / rect.width) * 100
      pct = Math.min(Math.max(pct, 5), 95)
      setIsReturning(false)
      setRevealX(pct)
    }

    const handleMouseMove = (event: MouseEvent) => {
      setPos(event.clientX)
    }
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches[0]) {
        setPos(event.touches[0].clientX)
      }
    }
    const handleMouseLeave = () => {
      setIsReturning(true)
      setRevealX(50)
    }

    hero.addEventListener('mousemove', handleMouseMove)
    hero.addEventListener('touchmove', handleTouchMove, { passive: true })
    hero.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      hero.removeEventListener('mousemove', handleMouseMove)
      hero.removeEventListener('touchmove', handleTouchMove)
      hero.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  return (
    <LinkWrapper
      href="https://shadowhint.com/ref/GERNAR"
      openInNewTab
      className={cn(`
        group relative flex overflow-hidden rounded-[calc(var(--radius)+var(--padding))]
        [--padding:12px]
        [--radius:8px]
        hover:ring-offset-2
      `, props.className)}
    >
      <style>
        {`
          .shadowhint-card-gradient {
            background: radial-gradient(116.05% 111.85% at 44.33% 115.39%, color(display-p3 .2048 .5316 .8202), #43a3e2 57.27%, color(display-p3 .2 .6039 .9608)), #fff;
          }

          .shadowhint-card-pattern {
            background: url('/assets/shadowhint/clouds-in-stillness.png') center/cover no-repeat;
          }

          .shadowhint-meeting-bg {
            background: url('/assets/shadowhint/meeting-bg.png') center/cover no-repeat;
          }
        `}
      </style>

      <section className="shadowhint-card-gradient relative flex w-full flex-col gap-2 bg-neutral-100 p-(--padding)">
        <div className="shadowhint-card-pattern absolute inset-0 z-0 size-full opacity-85" />

        <div className="relative z-10 flex flex-col justify-between gap-4 md:flex-row">
          <div className="shrink-0 grow-0 space-y-4 p-4 text-pretty text-white md:basis-1/3">
            <h2 className="text-3xl font-semibold">Скрытый ассистент для собеседований</h2>
            <p className="text-lg">
              ShadowHint слушает вопросы интервьюера, распознаёт экран и в реальном времени подсказывает точные ответы - оставаясь невидимым для собеседника.
            </p>
          </div>

          <div
            ref={heroRef}
            className={`
              shadowhint-meeting-bg relative flex aspect-[768/512] w-full shrink-0 grow-0 flex-col overflow-hidden
              rounded-[calc(var(--radius)+var(--padding))]
              md:basis-[calc(67%-3rem)]
            `}
            style={{ '--reveal-x': `${revealX}%` } as React.CSSProperties}
          >
            {/* Mask Layer */}
            <div
              className="pointer-events-none absolute inset-0 bg-black/55"
              style={{
                clipPath: `polygon(${revealX}% 0, 100% 0, 100% 100%, ${revealX}% 100%)`,
                transition: isReturning ? 'clip-path 0.4s ease-out' : 'none',
              }}
            />

            {/* Mask Line */}
            <div
              className={`
                pointer-events-none absolute top-0 bottom-0 z-20 w-0.5 bg-white/75
                shadow-[0_0_6px_rgba(255,255,255,0.6)]
              `}
              style={{
                left: `${revealX}%`,
                transform: 'translateX(-1px)',
                transition: isReturning ? 'left 0.4s ease-out' : 'none',
              }}
            />

            {/* Left Content (visible) */}
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                clipPath: `polygon(0 0, ${revealX}% 0, ${revealX}% 100%, 0 100%)`,
                transition: isReturning ? 'clip-path 0.4s ease-out' : 'none',
              }}
            >
              <CornerLabel position="left">Виден вам</CornerLabel>

              <div className="relative top-1/5">
                <UITopbar />
                <UIPanel />
              </div>
            </div>

            {/* Right Content (hidden label) */}
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                clipPath: `polygon(${revealX}% 0, 100% 0, 100% 100%, ${revealX}% 100%)`,
                transition: isReturning ? 'clip-path 0.4s ease-out' : 'none',
              }}
            >
              <CornerLabel position="right">Невидим для других</CornerLabel>
            </div>
          </div>
        </div>
      </section>
    </LinkWrapper>
  )
}

interface CornerLabelProps {
  children: React.ReactNode
  position: 'left' | 'right'
}

function CornerLabel(props: CornerLabelProps) {
  return (
    <div
      className={cn(
        `
          absolute top-4 rounded-lg border border-white/20 bg-black/55 px-2.5 py-1.5 text-xs font-semibold
          text-[#f2f4ff] backdrop-blur-lg
        `,
        props.position === 'left' ? 'left-2.5' : 'right-2.5 text-right',
      )}
    >
      {props.children}
    </div>
  )
}

function UITopbar() {
  return (
    <div className={cn(
      `
        absolute top-3 left-1/2 z-10 flex h-11 w-[380px] max-w-[420px] min-w-[360px] -translate-x-1/2 items-center
        justify-between gap-2.5 rounded-lg border border-white/10 bg-[rgba(16,18,26,0.78)] px-3 py-2.5 text-xs
        text-[#e6e8f3] shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl
        max-[450px]:right-3 max-[450px]:left-3 max-[450px]:w-[calc(100%-24px)] max-[450px]:max-w-none
        max-[450px]:min-w-0 max-[450px]:translate-x-0
      `,
    )}
    >
      <div
        className="size-7 rounded-lg bg-cover"
        style={{ backgroundImage: 'url(\'/assets/shadowhint/shadowhint-logo.webp\')' }}
        aria-label="ShadowHint"
      />
      <div className="grid size-5 place-items-center rounded-lg bg-gradient-to-br from-[#c18aff] to-[#8d63ff]">
        <div className="size-3 rounded-sm bg-[#f7f7ff]" />
      </div>
      <div className="flex items-center gap-1 max-[433px]:hidden">
        <span className="size-1.5 rounded-full bg-[#cdd2dd]" />
        <span className="size-1.5 rounded-full bg-[#cdd2dd]" />
        <span className="size-1.5 rounded-full bg-[#cdd2dd]" />
      </div>
      <span className="text-[#cfd4e6] max-[450px]:hidden">0%</span>
      <div className="flex gap-1">
        <div className={`
          inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold
          text-[#e6e8f3] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]
          max-[371px]:px-2
        `}
        >
          Ответ AI
        </div>
        <div className={`
          inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold
          text-[#e6e8f3] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)]
          max-[371px]:px-2
          max-[355px]:hidden
        `}
        >
          Скриншот
        </div>
      </div>
      <div className="grid gap-0.5 max-[397px]:hidden">
        <span className="h-0.5 w-4 rounded-sm bg-[#cfd4e6]" />
        <span className="h-0.5 w-4 rounded-sm bg-[#cfd4e6]" />
        <span className="h-0.5 w-4 rounded-sm bg-[#cfd4e6]" />
      </div>
    </div>
  )
}

function UIPanel() {
  return (
    <div className={cn(
      `
        absolute top-16 bottom-3 left-1/2 h-fit max-w-[420px] min-w-[360px] -translate-x-1/2 overflow-hidden rounded-xl
        border border-white/10 bg-[rgba(16,18,26,0.84)] p-3.5 px-4 text-[#e7e9f4] shadow-[0_18px_40px_rgba(0,0,0,0.35)]
        backdrop-blur-xl
      `,
      `
        max-[450px]:top-16 max-[450px]:right-3 max-[450px]:bottom-2.5 max-[450px]:left-3 max-[450px]:w-[calc(100%-24px)]
        max-[450px]:max-w-none max-[450px]:min-w-0 max-[450px]:translate-x-0
      `,
    )}
    >
      <div className="mb-2 text-[11px] font-extrabold tracking-[0.14em] text-[#7f7dff] uppercase">
        SHADOWHINT
      </div>
      <div className="mb-2.5 text-[13px] leading-relaxed text-[#e7e9f4]">
        <span className="font-bold text-[#ffdf7a]">Краткий ответ:</span>
        {' '}
        Создаётся копия списка, исходный не меняется.
      </div>
      <div className="-mt-1 mb-2.5 text-[13px] leading-relaxed text-[#e7e9f4]">
        В Python
        {' '}
        <span className="font-bold text-[#ffdf7a]">lst.copy()</span>
        {' '}
        или срез
        {' '}
        <span className="font-bold text-[#ffdf7a]">lst[:]</span>
        {' '}
        создают поверхностную копию.
      </div>
      <div className={`
        relative mt-1.5 rounded-lg border border-[#2a2d2e] bg-[#1e1e1e] p-3 pb-2.5 font-mono text-xs leading-relaxed
        text-[#d4d4d4] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
      `}
      >
        <div className={`
          absolute top-2 right-2.5 rounded-md border border-[#3a3d41] bg-[#2d2d2d] px-2 py-1 text-[11px] text-[#9cdcfe]
          lowercase
        `}
        >
          python
        </div>
        <code className="whitespace-pre">
          <span className="font-bold text-[#c586c0]">original</span>
          {' '}
          <span className="text-[#9cdcfe]">=</span>
          {' '}
          <span className="text-[#b5cea8]">[1, 2, 3]</span>
        </code>
      </div>
    </div>
  )
}
